/**
 * Las personas que contestaron pidiendo la baja («BAJA») y siguen recibiendo. Snov las cuenta como una respuesta más
 * y NO las saca de las próximas campañas: si nadie las pasa a «No enviar», les vuelve a escribir (y la casilla junta
 * denuncias de spam).
 *
 * El equipo las revisa y las pasa: el panel trae a cada una con lo que contestó (la detección de «BAJA» es por el
 * texto y puede equivocarse), todas marcadas, y se pasan sólo las que quedan marcadas. Van a la lista de «No enviar» de
 * Snov (una sola para toda la cuenta: no les escribe ninguna campaña) y a las bajas de mail de Activa. Una persona de
 * un cliente ve el aviso, sin el botón (escribe en la cuenta de Snov de Deenex).
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, UserMinus } from "lucide-react";
import { emailsApi } from "../api";
import type { BajaPendiente, ResultadoBajas } from "../types";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { num, plural } from "../../campana-nueva/utils/format";
import { R_EMAILS, diaMesHora } from "../logic/emails";

export function AvisoBajas({ pendientes, pidieron, rutaPersonas, campana, onListo }: {
  /** contestaron pidiendo la baja y no están en «No enviar» */
  pendientes: number;
  /** las que contestaron pidiendo la baja (para decir que ya están todas, si no falta ninguna) */
  pidieron: number;
  rutaPersonas: string;
  /** sólo las de esta campaña (en su detalle) */
  campana?: number;
  /** después de pasarlas: la página vuelve a pedir lo suyo */
  onListo?: () => void;
}) {
  const equipo = useEsEquipoDeenex();
  const [abierto, setAbierto] = useState(false);
  const [lista, setLista] = useState<BajaPendiente[] | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [cargando, setCargando] = useState(false);
  const [pasando, setPasando] = useState(false);
  const [error, setError] = useState("");
  const [hecho, setHecho] = useState<ResultadoBajas | null>(null);

  if (hecho) {
    return (
      <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" role="status">
        <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-emerald-600" />
        <div>
          <p className="font-semibold">
            Listo: {num(hecho.pasadas)} {plural(hecho.pasadas, "persona pasó", "personas pasaron")} a «No enviar» en Snov
            {hecho.enActiva ? ` y ${num(hecho.enActiva)} a las bajas de mail de Activa` : ""}.
          </p>
          {hecho.yaEstaban > 0 && <p className="mt-0.5 text-emerald-800">{num(hecho.yaEstaban)} ya {plural(hecho.yaEstaban, "estaba", "estaban")} en la lista de Snov.</p>}
          {hecho.errores.length > 0 && <p className="mt-0.5 text-amber-800">No se pudieron registrar en Activa: {hecho.errores.join(" · ")}</p>}
          {pendientes > hecho.pasadas && <p className="mt-0.5 text-emerald-800">Las que dejaste sin marcar siguen en la lista para revisar.</p>}
        </div>
      </div>
    );
  }

  if (!(pendientes > 0)) {
    if (!(pidieron > 0)) return null;
    return (
      <p className="inline-flex items-center gap-1.5 text-xs text-emerald-700" role="status">
        <CheckCircle2 size={13} /> Quienes pidieron la baja contestando ya están en «No enviar».
      </p>
    );
  }

  const abrir = async () => {
    setAbierto(true);
    setCargando(true);
    setError("");
    try {
      const l = leerRespuesta<BajaPendiente[]>(await emailsApi.bajasPendientes(campana));
      if (!l.ok || !l.data) setError(l.ok ? "El servidor no devolvió la lista." : textoError(l));
      else {
        setLista(l.data);
        setMarcados(new Set(l.data.map((p) => p.email)));
      }
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setCargando(false);
    }
  };

  const pasar = async () => {
    if (!marcados.size) return;
    setPasando(true);
    setError("");
    try {
      const l = leerRespuesta<ResultadoBajas>(await emailsApi.pasarBajas([...marcados], campana));
      if (!l.ok || !l.data) setError(l.ok ? "El servidor no devolvió el resultado." : textoError(l));
      else {
        setHecho(l.data);
        onListo?.();
      }
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setPasando(false);
    }
  };

  const alternar = (email: string) => setMarcados((m) => {
    const n = new Set(m);
    if (n.has(email)) n.delete(email); else n.add(email);
    return n;
  });

  return (
    <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900" role="status">
      <UserMinus size={18} className="mt-0.5 flex-shrink-0 text-red-600" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">
          {num(pendientes)} {plural(pendientes, "persona contestó pidiendo la baja y sigue recibiendo", "personas contestaron pidiendo la baja y siguen recibiendo")}.
        </p>
        <p className="mt-0.5 text-red-800">
          Snov no las saca solas: si nadie las pasa a «No enviar», la próxima campaña les vuelve a escribir.{" "}
          <Link to={rutaPersonas} className="font-semibold underline">Ver quiénes son</Link>
        </p>
        {!equipo && <p className="mt-1 text-red-800">Pedile al equipo de Deenex que las pase a «No enviar».</p>}
        {equipo && !abierto && (
          <button type="button" onClick={() => void abrir()}
            className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700">
            Revisar y pasar a «No enviar»
          </button>
        )}
        {equipo && abierto && (
          <div className="mt-2 rounded-xl border border-red-200 bg-white p-3 text-slate-700">
            {cargando ? (
              <p className="inline-flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Trayendo lo que contestó cada una…</p>
            ) : lista && !lista.length ? (
              <p className="text-sm">Ya no falta nadie: se pasaron mientras tanto.</p>
            ) : lista ? (
              <>
                <p className="text-sm">
                  Revisá lo que contestó cada una (la «BAJA» se detecta por el texto y puede equivocarse) y desmarcá a quien no la pidió.
                  Las marcadas van a la lista de «No enviar» de Snov, que es una sola para toda la cuenta: no les va a escribir ninguna campaña,
                  de ningún comercio. También quedan en las bajas de mail de Activa.
                </p>
                <ul className="mt-2 max-h-80 overflow-y-auto divide-y divide-slate-100 rounded-lg border border-slate-100">
                  {lista.map((p) => (
                    <li key={p.email} className="flex items-start gap-2.5 px-3 py-2">
                      <input id={`baja-${p.id}`} type="checkbox" className="mt-1" checked={marcados.has(p.email)} onChange={() => alternar(p.email)} />
                      <label htmlFor={`baja-${p.id}`} className="min-w-0 flex-1 cursor-pointer">
                        <span className="block text-sm">
                          <span className="font-semibold text-slate-900">{p.nombre || "Sin nombre"}</span>{" "}
                          <span className="text-xs text-slate-500 break-all">{p.email}</span>
                        </span>
                        <span className="block text-sm text-slate-800 whitespace-pre-wrap break-words line-clamp-3">«{p.texto || "(sin texto)"}»</span>
                        <span className="block text-[11px] text-slate-400">{p.campana.titulo || `Campaña ${p.campana.id}`}{p.recibidaEn ? ` · ${diaMesHora(p.recibidaEn)}` : ""}</span>
                      </label>
                      <Link to={`${R_EMAILS}/personas/${encodeURIComponent(p.id)}`} className="mt-0.5 text-xs font-semibold text-violet-700 hover:underline whitespace-nowrap">Ficha</Link>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={() => void pasar()} disabled={pasando || !marcados.size}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                    {pasando && <Loader2 size={14} className="animate-spin" />}
                    {marcados.size === 1 ? "Pasar la marcada" : `Pasar las ${num(marcados.size)} marcadas`}
                  </button>
                  <button type="button" onClick={() => setAbierto(false)} disabled={pasando}
                    className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    Cancelar
                  </button>
                </div>
              </>
            ) : null}
            {error && (
              <p className="mt-2 text-sm text-red-800" role="alert">
                {error}{" "}
                {!lista && <button type="button" onClick={() => void abrir()} className="font-semibold underline">Reintentar</button>}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AvisoBajas;
